<template>
  <div class="fs-exclude">
    <s-row class="heading-decsription">
      <s-col span="11" class="pad-bottom-none">
        <h3>
          VAT Recoverability Matrix
        </h3>
        <p class="margin-bottom-md">
          Understand VAT recoverability across countries using a recoverability matrix.
          The matrix displays each tax code in a table so you can easily see the country
          and the VAT recoverability percentage based on the jurisdiction's rules.
        </p>
      </s-col>
      <s-col span="10" class="pad-top-none pad-bottom-md">
        <s-alert status="info" nodismiss="">
          <div class="flex">
            The VAT recoverability matrix is updated regularly with the latest legislation.
            <a
              href="https://help.avalara.com"
              class="margin-left-sm"
            >Learn more</a>
          </div>
        </s-alert>
      </s-col>
    </s-row>
    <s-row>
      <s-col span="5" class="vat-recoverability-search">
        <s-box>
          <s-row>
            <s-col class="pad-bottom-none" span="3">
              <img
                :src="
                  `${$store.getters.assetsPath}/img/taxability_search.png`
                "/>
            </s-col>
            <s-col class="pad-bottom-none" span="9">
              <div class="left pad-left-sm">
                <h3 class="header">
                  Build a VAT recoverability matrix
                </h3>
                <div>
                  Build a custom VAT recoverability matrix by searching for specific tax
                  codes and countries.
                </div>
                <h3>
                  <a>
                    <button
                      @click="onVATRecoverabilityBuild"
                      class="primary"
                      title="build your own VAT recoverability matrix"
                    >
                      Build
                    </button>
                  </a>
                </h3>
              </div>
            </s-col>
          </s-row>
        </s-box>
      </s-col>
      <s-col span="5">
        <s-box>
          <s-row>
            <s-col class="pad-bottom-none" span="3">
              <img
                :src="
                  `${$store.getters.assetsPath}/img/taxability_download.png`
                "/>
            </s-col>
            <s-col class="pad-bottom-none" span="9">
              <div class="left pad-left-sm">
                <h3 class="header">
                  Download a full VAT recoverability matrix
                </h3>
                <div>
                  <label for="select-country">Select country</label>
                  <s-select
                    search
                    inputid="select-country"
                    id="select-country"
                    placeholder="Select Country"
                    :optionsList="JSON.stringify(allCountriesData)"
                    @s-select="onCountrySelected"
                  >
                  </s-select>
                </div>
                <h3>
                  <a
                    :href="
                      '/api/vat-recoverability/matrix?country_code=' +
                        selectedCountry
                    "
                  >
                    <button
                      @click="captureFullVATRecoverabilityDownload"
                      class="primary icon-leading"
                      title="download full VAT recoverability matrix"
                      link
                    >
                      <s-icon name="download"></s-icon>
                      Download (.xlsx)
                    </button>
                  </a>
                </h3>
              </div>
            </s-col>
          </s-row>
        </s-box>
      </s-col>
    </s-row>
  </div>
</template>
<script lang="ts">
import {Component, Vue, Provide} from "vue-property-decorator";
import analyticsLayerUtil from "./../utils/index";

interface SelectOption {
  label: string;
  value: string;
  selected?: boolean;
}

interface JurisdictionResponse {
  country_code: string;
  country_name: string;
  jurisdictions: object[];
}

@Component({})
export default class VATRecoverability extends Vue {
  @Provide("$api") $api: any;
  allCountriesData: SelectOption[] = [];
  selectedCountry = "ALL";

  mounted() {
    this.$api
      .get(`/api/jurisdictions/`)
      .then((response) => {
        const allJurisdictionsForCountries: JurisdictionResponse[] =
          response.data;

        allJurisdictionsForCountries.forEach((country) => {
          this.allCountriesData.push({
            label: country.country_name,
            value: country.country_code
          });
        });

        this.allCountriesData.sort((a, b) => a.label < b.label ? -1 : 1);
        this.allCountriesData.splice(0, 0, {label: "All Countries", value: "ALL"});
      })
      .catch((error) => this.$store.commit("stopLoading"));
  }

  public onCountrySelected(e: any) {
    this.selectedCountry = e.detail.item.value;
  }

  public onVATRecoverabilityBuild(e: any) {
    analyticsLayerUtil.commitDataLayer();
    this.$router.push({
      path: `/vat-recoverability/search`,
    });
  }

  public captureFullVATRecoverabilityDownload() {
    const props = {
      vatRecoverabilityMatrix: {
        vatRecoverabilityMatrixInfo: {
          selectedCountry: this.selectedCountry,
        },
      },
    };
    analyticsLayerUtil.addToDataLayer(props);
    if (this.$store.getters.analyticsEnabled) {
      window._satellite.track("vat_recoverability_full_download");
    }
  }
}
</script>
<style scoped lang="scss">
.heading-decsription {
  * {
    line-height: 1.4rem;
  }

  p {
    font-size: 0.92rem;
    font-weight: 400;
    color: var(--color-gray-dark);
  }

  h3 {
    font-weight: bold;
    font-size: 1.7rem;
  }
}

.header {
  font-size: 1.1rem;
}

.vat-recoverability-search {
  s-box {
    height: 100%;
  }
}
</style>
